import React, {useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';
import {from} from "rxjs";
import {map} from "rxjs/operators";
import AdminMessageFiles from "./AdminMessageFiles";
import PaginatedTable from "../../../components/Block/Table/PaginatedTable";
import LoadSpinner from "../../../components/Common/LoadSpinner";
import Pagination from "../../../components/Block/Pagination/Pagination";
import ConversationDetailsModal from "./ConversationDetailsModal";
import {adminGetConversationDetails} from "../../../services/api/admin";
import {moment} from "../../../services/moment-extensions";

function AdminConversationDetails() {
    const {id, page, per_page} = useParams();

    const [conversationPagination, setConversationPagination] = useState({
        total: 0,
        count: 0,
        per_page: parseInt(per_page, 10) || 10,
        current_page: parseInt(page, 10) || 1,
        total_pages: 1
    });

    const [conversation, setConversation] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);

    const [activeFilterOptions, setActiveFilterOptions] = useState({
        page: conversationPagination.current_page,
        per_page: conversationPagination.per_page
    });

    const [conversationsFetched, setConversationsFetched] = useState(false);

    const getParticipants = (result, accountID) => {
        return result.participants?.filter(item => item.id !== accountID).map(participant => `${participant.first_name} ${participant.last_name}`).join(',');
    }

    const fetchConversation = useCallback((id, activeFilterOptions) => {
        setConversationsFetched(true);
        return from(adminGetConversationDetails(id, activeFilterOptions))
            .pipe(
                map((response) => response)
            )
            .subscribe(
                (result) => {
                    setConversation(result);

                    const transformedData = result.messages.data.map((message) => ({

                        id: {data: message.id, classes: "sl-min-w-10"},
                        date: {data: moment(message.created_at * 1000).toDateLongWithToday(), classes: ""},
                        sender: {data: message.account.first_name + " " + message.account.last_name, classes: ""},
                        participants: {data: getParticipants(result, message.account.id), classes: ""},
                        message: {
                            data: message.content,
                            classes: "sl-text-left sl-cursor-pointer",
                            contentClasses: 'sl-ellipsis-three-line sl-max-w-388',
                            onClick: () => setSelectedRow({
                                id: message.id,
                                message: message.content
                            })
                        },
                        files: {
                            data: <AdminMessageFiles conversationID={id} files={message.files} shortName={true}/>,
                            classes: ""
                        },
                    }));

                    setTableData(transformedData);
                    setConversationPagination(result.messages.meta.pagination);
                    setActiveFilterOptions({
                        page: result.messages.meta.pagination.current_page,
                        per_page: result.messages.meta.pagination.per_page
                    })
                },
                (error) => {
                    console.error('Error fetching messages:', error);
                }
            );
    }, []);

    if (!conversationsFetched) {
        fetchConversation(id, activeFilterOptions);
    }

    React.useEffect(() => {
        setActiveFilterOptions({
            page: page,
            per_page: per_page
        })
        setConversationsFetched(false);
    }, [page, per_page]);

    const headers = [
        {data: 'ID', classes: "sl-min-w-6"},
        {data: 'Date', classes: ""},
        {data: 'Sender', classes: ""},
        {data: 'Participants', classes: ""},
        {data: 'Message', classes: "sl-text-left"},
        {data: 'Files', classes: ""}
    ]; // Table headers


    return (
        <div>
            {selectedRow && <ConversationDetailsModal onClose={() => setSelectedRow(null)} data={selectedRow}/>}
            <main className="sl-main sl-mt-8 sl-min ">
                <div className="sl-container sl-m-auto sl-my-8">
                    <h1>{(conversation.topic) ? "Topic: " + conversation.topic : "ID: " + id}</h1>

                    {tableData.length > 0 ? (
                        <div className="sl-table-container">
                            <PaginatedTable
                                headers={headers}
                                data={tableData}
                            />
                        </div>
                    ) : (
                        <div className="sl-self-center sl-mt-3 sl-mb-21">
                            <LoadSpinner/>
                        </div>
                    )}
                    {conversationPagination.total_pages > 1 && (
                        <Pagination paginationData={conversationPagination} basePath={`/admin/conversation/` + id}
                                    paginator={conversationPagination}/>
                    )}
                </div>
            </main>
        </div>
    );
}

export default AdminConversationDetails;
